/** One queue per authenticated session; a session epoch also protects A → B → A switches. */
export class StoryWriteQueue {
  private account = ''
  private epoch = 0
  private tails = new Map<string, Promise<unknown>>()
  private controllers = new Set<AbortController>()
  private uncertain = new Set<string>()

  setAccount(account: string) {
    if (account === this.account) return
    this.account = account
    this.epoch++
    for (const controller of this.controllers) controller.abort()
    this.controllers.clear()
    this.tails.clear()
    this.uncertain.clear()
  }
  isAccount(account: string) {
    return Boolean(account) && account === this.account
  }

  run<T>(
    account: string,
    storyId: string,
    task: (signal: AbortSignal) => Promise<T>,
    reconcile?: (signal: AbortSignal) => Promise<void>
  ): Promise<T> {
    const epoch = this.epoch
    const key = `${account}:${storyId}`
    const assertSession = () => {
      if (!this.isAccount(account) || epoch !== this.epoch)
        throw new DOMException('Story session ended', 'AbortError')
    }
    const previous = this.tails.get(key) ?? Promise.resolve()
    const result = previous
      .catch(() => undefined)
      .then(async () => {
        assertSession()
        const controller = new AbortController()
        this.controllers.add(controller)
        try {
          if (this.uncertain.has(key) && reconcile) {
            await reconcile(controller.signal)
            assertSession()
            this.uncertain.delete(key)
          }
          const response = await task(controller.signal)
          assertSession()
          return response
        } catch (error) {
          if (!controller.signal.aborted && epoch === this.epoch)
            this.uncertain.add(key)
          throw error
        } finally {
          this.controllers.delete(controller)
        }
      })
    this.tails.set(key, result)
    // Both branches observe rejections and release settled queues without masking the caller's error.
    const cleanup = () => {
      if (this.tails.get(key) === result) this.tails.delete(key)
    }
    void result.then(cleanup, cleanup)
    return result
  }
}

export const storyWrites = new StoryWriteQueue()
